import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Phone, User, Award, Clock, Shield, Target, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const LawnCareWebsiteDesigner = () => {
  const faqs = [
    {
      question: "What does a lawn care website designer do?",
      answer: "A lawn care website designer creates professional websites specifically for lawn care businesses. They handle layout, visuals, copywriting, SEO structure, and conversion optimization—tailored to attract local homeowners searching for lawn services."
    },
    {
      question: "How much does a lawn care website designer charge?",
      answer: "Freelance designers typically charge $1,000–$5,000 for a one-time build. Agency designers or subscription services range from $200–$500/month with ongoing support included. Prices vary based on experience and deliverables."
    },
    {
      question: "Should I hire a freelance designer or an agency for my lawn care site?",
      answer: "Freelancers offer lower upfront costs but may lack ongoing support. Agencies provide structured processes and maintenance. A niche-focused agency combines both—specialized expertise with reliable support."
    },
    {
      question: "What should I look for when hiring a lawn care website designer?",
      answer: "Look for: experience with lawn care or service businesses, portfolio examples in your industry, clear communication, and ongoing support options. Ask about SEO knowledge and mobile optimization."
    },
    {
      question: "How long does it take for a designer to build my lawn care website?",
      answer: "Freelance designers typically take 2–6 weeks. Agencies can take 4–8 weeks. Specialized designers like us deliver in 10–14 days because of proven templates and niche focus."
    },
    {
      question: "Will the designer help with SEO, or just the visual design?",
      answer: "A quality lawn care website designer builds SEO into the foundation—proper headings, meta tags, schema markup, and mobile optimization. Pure 'visual designers' may not understand SEO, so ask upfront."
    },
    {
      question: "Do I need to provide content and photos to the designer?",
      answer: "You'll need to provide photos of your work (phone photos are fine) and your service list. Good designers handle copywriting, layout, and SEO structure. The more photos you provide, the better."
    },
    {
      question: "Can a designer help me rank in local Google searches?",
      answer: "A good designer builds the foundation—service area pages, proper schema, fast loading. But ongoing ranking depends on content quality, Google Business Profile, and sometimes additional SEO work."
    },
    {
      question: "What happens after my lawn care website launches?",
      answer: "With freelancers, support often ends after launch. With agencies or subscription models, you get ongoing maintenance, updates, and support. Make sure you understand post-launch support before hiring."
    },
    {
      question: "How do I evaluate if a designer is right for my lawn care business?",
      answer: "Ask for lawn care-specific examples. If they can't show relevant work, they may not understand your industry. A discovery call should focus on your business needs, not just design preferences."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-designer/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Lawn care website designers specializing in websites for lawn care and landscaping companies."
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-website-designer/#webpage",
    "name": "Lawn Care Website Designer | Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com/lawn-care-website-designer/",
    "description": "Find a lawn care website designer who understands your industry. We design websites exclusively for lawn care companies. $0 upfront.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    }
  };

  return (
    <>
      <SEO 
        title="Lawn Care Website Designer | Leaf Rush"
        description="Find a lawn care website designer who understands your industry. We design websites exclusively for lawn care companies. $0 upfront."
        keywords="lawn care website designer, lawn care web designer, website designer for lawn care, lawn care site designer"
        canonical="https://www.leafrushmarketing.com/lawn-care-website-designer/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-website-designer/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="container px-4 md:px-6 relative z-10">
            <div className="mx-auto max-w-4xl text-center space-y-6">
              <Badge variant="secondary" className="mb-4">
                Niche-Focused Designer
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Lawn Care Website Designer Who Understands Your Business
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                Looking for a lawn care website designer? We design websites exclusively for lawn care businesses—from solo mowers to multi-crew operations.
              </p>
              
              {/* Quick Answer Box */}
              <Card className="bg-primary/5 border-primary/20 text-left max-w-2xl mx-auto">
                <CardContent className="pt-6">
                  <p className="font-semibold text-primary mb-2">Quick Answer:</p>
                  <p className="text-muted-foreground">
                    A lawn care website designer creates professional sites for lawn care businesses. Freelancers charge $1,000–$5,000 one-time; agencies charge $200–$500/month. Look for industry experience and SEO knowledge.
                  </p>
                </CardContent>
              </Card>

              {/* Not Sure Callout */}
              <div className="bg-accent/30 rounded-lg p-4 text-sm text-muted-foreground max-w-2xl mx-auto">
                <strong>Not sure if this is what you need?</strong> If you're exploring all your website options, visit our main{" "}
                <NavLink to="/lawn-care-website-design/" className="text-primary underline hover:no-underline">
                  lawn care website design
                </NavLink>{" "}
                page for a complete overview.
              </div>
            </div>
          </div>
        </section>

        {/* What to Look For */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                What to Look for in a Lawn Care Website Designer
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex gap-3">
                  <Target className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Industry Experience</p>
                    <p className="text-sm text-muted-foreground">
                      They should understand lawn care services, seasonal patterns, and what homeowners look for.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">SEO Knowledge</p>
                    <p className="text-sm text-muted-foreground">
                      Not just pretty designs—they should understand local SEO, service pages, and conversion optimization.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Award className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Portfolio Examples</p>
                    <p className="text-sm text-muted-foreground">
                      Ask to see lawn care or service business websites they've designed. No examples = red flag.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold">Ongoing Support</p>
                    <p className="text-sm text-muted-foreground">
                      What happens after launch? Good designers offer maintenance and update options.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Designer vs Agency */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Freelance Designer vs. Agency: Which is Right?
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Factor</th>
                      <th className="text-left py-3 px-4">Freelance Designer</th>
                      <th className="text-left py-3 px-4">Niche Agency (Us)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Cost</td>
                      <td className="py-3 px-4">$1,000–$5,000 one-time</td>
                      <td className="py-3 px-4">$200–$300/mo, $0 upfront</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Timeline</td>
                      <td className="py-3 px-4">2–6 weeks</td>
                      <td className="py-3 px-4">10–14 days</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Ongoing Support</td>
                      <td className="py-3 px-4">Often limited/extra cost</td>
                      <td className="py-3 px-4">Included in subscription</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">Industry Focus</td>
                      <td className="py-3 px-4">Usually generalist</td>
                      <td className="py-3 px-4">Lawn care/landscaping only</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 px-4 font-semibold">SEO Included</td>
                      <td className="py-3 px-4">Variable</td>
                      <td className="py-3 px-4">Built into every site</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                See full cost breakdown:{" "}
                <NavLink to="/lawn-care-website-cost/" className="text-primary underline hover:no-underline">
                  lawn care website cost
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary underline hover:no-underline">
                  website subscription pricing
                </NavLink>
              </p>
            </div>
          </div>
        </section>

        {/* What We Do Differently */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                How We Design Lawn Care Websites
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Conversion-First Layout</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Every element is designed to turn visitors into quote requests and phone calls.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Service Page Structure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Dedicated pages for mowing, fertilization, aeration, cleanups—each optimized to rank.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Local SEO Built-In</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Service area pages, schema markup, and proper headings to rank where you work.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="text-center mt-8">
                <p className="text-muted-foreground mb-4">
                  See{" "}
                  <NavLink to="/lawn-care-website-examples/" className="text-primary underline hover:no-underline">
                    lawn care website examples
                  </NavLink>{" "}
                  and{" "}
                  <NavLink to="/service-pages-that-rank/" className="text-primary underline hover:no-underline">
                    service pages that rank
                  </NavLink>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Frequently Asked Questions About Lawn Care Website Designers
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="py-12 md:py-16 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Related Guides
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <NavLink to="/lawn-care-website-design/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Design</h3>
                  <p className="text-sm text-muted-foreground">Complete guide to professional lawn care websites.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-content/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website Content</h3>
                  <p className="text-sm text-muted-foreground">What content you need for your site.</p>
                </NavLink>
                <NavLink to="/lawn-care-website-seo/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">Lawn Care Website SEO</h3>
                  <p className="text-sm text-muted-foreground">SEO fundamentals for lawn care sites.</p>
                </NavLink>
                <NavLink to="/resources/" className="block p-4 rounded-lg border bg-background hover:border-primary transition-colors">
                  <h3 className="font-semibold mb-1">All Resources</h3>
                  <p className="text-sm text-muted-foreground">Browse all guides and resources.</p>
                </NavLink>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="schedule-call" className="py-16 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Ready to Hire a Lawn Care Website Designer?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Let's discuss your lawn care business and see if we're a fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <NavLink to="/contact#get-started">
                  <Button size="lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Get Started
                  </Button>
                </NavLink>
                <NavLink to="/websites/">
                  <Button size="lg" variant="outline">
                    View Website Subscription Pricing
                  </Button>
                </NavLink>
              </div>
            </div>
            <div className="max-w-2xl mx-auto mt-10">
              <CalendlyEmbed />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareWebsiteDesigner;
